import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { contentNavigationGroups } from '@/data/contentNavigation';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { contentNavigationIconMap } from './contentNavigationIcons';

export function ContentDropdown() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isActive = contentNavigationGroups.some((group) => group.items.some((item) => pathname === item.href || pathname.startsWith(`${item.href}/`)));

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-all duration-200',
            open || isActive ? 'bg-secondary/10 text-secondary' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800',
          )}
          aria-label="Open Bookstera resources"
        >
          Publishing Resources <ChevronDown className={cn('h-3.5 w-3.5 transition-transform duration-200', open && 'rotate-180')} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="center"
        sideOffset={10}
        className="max-h-[min(80vh,var(--radix-dropdown-menu-content-available-height))] w-[min(1100px,calc(100vw-2rem))] overflow-y-auto rounded-3xl border border-slate-200 bg-white p-3 text-slate-900 shadow-2xl"
      >
        <div className="columns-1 gap-2 sm:columns-2 lg:columns-3">
          {contentNavigationGroups.map((group) => (
            <section
              key={group.label}
              className={cn(
                'mb-2 rounded-2xl bg-slate-50 p-2 [box-decoration-break:clone]',
                group.label === 'Marketing software comparisons' && 'sm:break-before-column',
                group.label === 'Guides and editorial' && 'lg:break-inside-avoid-column',
                group.label === 'Use cases' && 'lg:break-before-column',
              )}
              aria-label={group.label}
            >
              <div className="break-after-avoid px-3 pb-2 pt-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-600">{group.label}</div>
              <div>
                {group.items.map((item) => {
                  const Icon = contentNavigationIconMap[item.icon];
                  const itemActive = pathname === item.href || pathname.startsWith(`${item.href}/`);
                  return (
                    <DropdownMenuItem key={item.href} asChild className="mb-0.5 cursor-pointer break-inside-avoid p-0 focus:bg-white focus:text-slate-900">
                      <Link
                        to={item.href}
                        onClick={() => setOpen(false)}
                        className={cn('group flex cursor-pointer items-start gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-white focus:bg-white', itemActive && 'bg-white ring-1 ring-secondary/15')}
                      >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-orange-800">{item.emoji ? <span className="text-base" aria-hidden="true">{item.emoji}</span> : <Icon className="h-4 w-4" />}</span>
                        <span className="min-w-0">
                          <span className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-slate-900">
                            {item.name}
                            {item.badge && <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">{item.badge}</span>}
                          </span>
                          <span className="mt-0.5 block text-xs leading-5 text-slate-700">{item.description}</span>
                        </span>
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
